import React from "react";
import { FieldErrors, FieldValues, UseFormRegister } from "react-hook-form";
import { restrictIntialSpace } from "../../core/services/sharedService";
import { useAppSelector } from "../../core/redux/hooks";

interface MultiPMSConnectionProps {
    register: UseFormRegister<FieldValues>;
    errors: FieldErrors<FieldValues>;
    pms_provider: string;
    onProviderChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    resetField: (name: string) => void;
    setValue: (name: string, value: any) => void;
    ssm_user_id: string;
    ssm_password: string;
    ssm_base_url: string;
    application_id: string;
    company_id: string;
    storageapi_key: string;
    monument_base_url: string;
    monument_portfolio: string;
    monument_api_key: string;
    page: string;
}

const MultiPMSconnection = ({ register, errors, pms_provider, onProviderChange, resetField, ssm_user_id, ssm_password, ssm_base_url, application_id, company_id, storageapi_key, monument_base_url, monument_portfolio, monument_api_key, page, setValue }: MultiPMSConnectionProps) => {
    const LoginUserDetails = useAppSelector((state) => state.auth.users);

    // Fields owned by each provider + the prop-seeded value for each field. On a
    // provider change we clear the fields of the providers no longer selected and,
    // when editing, re-seed the newly-selected provider's fields from props.
    const providerFields: Record<string, string[]> = {
        ssm: ['ssm_user_id', 'ssm_password', 'ssm_base_url'],
        hummingbird: ['application_id', 'company_id', 'storageapi_key'],
        monument: ['monument_base_url', 'monument_portfolio', 'monument_api_key'],
    };
    const fieldPropValues: Record<string, any> = {
        ssm_user_id, ssm_password, ssm_base_url,
        application_id, company_id, storageapi_key,
        monument_base_url, monument_portfolio, monument_api_key,
    };
    const resetFieldValuesWhenProviderChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = e.target.value;
        Object.keys(providerFields).forEach((provider) => {
            if (provider === selected) {
                if (page === "edit_location") {
                    providerFields[provider].forEach((field) => setValue(field, fieldPropValues[field]));
                }
            } else {
                // Clear every non-selected provider's fields with setValue('') rather
                // than resetField(): resetField is a no-op on inputs not currently
                // mounted (RHF only tracks registered fields) and otherwise resets to
                // the original defaultValue, so a provider whose inputs were never
                // rendered this session could keep its prop-seeded value and be
                // submitted. setValue always clears _formValues, mounted or not.
                providerFields[provider].forEach((field) => setValue(field, ''));
            }
        });
    };
    React.useEffect(() => {
        setValue('application_id', application_id);
        setValue('company_id', company_id);
        setValue('storageapi_key', storageapi_key);
        setValue('ssm_user_id', ssm_user_id);
        setValue('ssm_password', ssm_password);
        setValue('ssm_base_url', ssm_base_url);
        setValue('monument_base_url', monument_base_url);
        setValue('monument_portfolio', monument_portfolio);
        setValue('monument_api_key', monument_api_key);
    }, []);
    return (
        <>
            <fieldset className="border col-md-8 p-2 mt-2 mb-4" style={{ borderRadius: '5px' }}>
                <legend className="float-none w-auto m-0">PMS Integration Information</legend>
                <div className="row mb-2">
                    <div className="col-md-6">
                        <label>PMS</label>
                    </div>
                    <div className="col-md-6">
                        <select
                            className="form-select"
                            value={pms_provider}
                            onChange={(e) => { onProviderChange(e); resetFieldValuesWhenProviderChanged(e); }}
                            disabled={LoginUserDetails.role !== "super_admin"}
                        >
                            <option value="ssm">SSM</option>
                            <option value="hummingbird">HummingBird</option>
                            <option value="monument">Monument</option>
                        </select>
                    </div>
                </div>

                {
                    pms_provider === "ssm" ?
                        (
                            <>
                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <label>SSM Base URL</label>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            {...register('ssm_base_url', { required: true })}
                                            onKeyDown={(e) => restrictIntialSpace(e)}
                                            defaultValue={ssm_base_url || ""}
                                            placeholder="https://customer-wrapper.ssm-erp.com"
                                            className="form-control" readOnly={(LoginUserDetails.role == "super_admin" ? false : true)}
                                        />
                                        {errors.ssm_base_url?.type === "required" && <small className="text-danger">SSM Base URL is required</small>}
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <label>SSM USER ID</label>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            {...register('ssm_user_id', { required: true })}
                                            onKeyDown={(e) => restrictIntialSpace(e)}
                                            defaultValue={ssm_user_id}
                                            className="form-control" readOnly={(LoginUserDetails.role == "super_admin" ? false : true)}
                                        />
                                        {errors.ssm_user_id?.type === "required" && <small className="text-danger">SSM User ID is required</small>}
                                    </div>
                                </div>

                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <label>SSM PASSWORD</label>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            {...register('ssm_password', { required: true })}
                                            onKeyDown={(e) => restrictIntialSpace(e)}
                                            defaultValue={ssm_password}
                                            className="form-control" readOnly={(LoginUserDetails.role == "super_admin" ? false : true)}
                                        />
                                        {errors.ssm_password?.type === "required" && <small className="text-danger">SSM PASSWORD is required</small>}
                                    </div>
                                </div>
                            </>

                        ) : pms_provider === "monument" ?
                        (
                            <>
                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <label>Monument Base URL</label>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            {...register('monument_base_url', { required: true })}
                                            onKeyDown={(e) => restrictIntialSpace(e)}
                                            defaultValue={monument_base_url || ""}
                                            className="form-control" readOnly={(LoginUserDetails.role == "super_admin" ? false : true)}
                                        />
                                        {errors.monument_base_url?.type === "required" && <small className="text-danger">Monument Base URL is required</small>}
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <label>Monument Portfolio</label>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            {...register('monument_portfolio', { required: true })}
                                            onKeyDown={(e) => restrictIntialSpace(e)}
                                            defaultValue={monument_portfolio}
                                            className="form-control" readOnly={(LoginUserDetails.role == "super_admin" ? false : true)}
                                        />
                                        {errors.monument_portfolio?.type === "required" && <small className="text-danger">Monument Portfolio is required</small>}
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <label>Monument API Key</label>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            {...register('monument_api_key', { required: true })}
                                            onKeyDown={(e) => restrictIntialSpace(e)}
                                            defaultValue={monument_api_key}
                                            className="form-control" readOnly={(LoginUserDetails.role == "super_admin" ? false : true)}
                                        />
                                        {errors.monument_api_key?.type === "required" && <small className="text-danger">Monument API Key is required</small>}
                                    </div>
                                </div>
                            </>
                        ) :
                        (
                            <>
                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <label>RCM Application ID</label>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            {...register('application_id', { required: true })}
                                            onKeyDown={(e) => restrictIntialSpace(e)}
                                            defaultValue={application_id}
                                            className="form-control" readOnly={(LoginUserDetails.role == "super_admin" ? false : true)}
                                        />
                                        {errors.application_id?.type === "required" && <small className="text-danger">RCM Application ID is required</small>}
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <label>Customer PMS Company ID</label>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            {...register('company_id', { required: true })}
                                            onKeyDown={(e) => { restrictIntialSpace(e) }}
                                            defaultValue={company_id}
                                            className="form-control" readOnly={(LoginUserDetails.role == "super_admin" ? false : true)}
                                        />
                                        {errors.company_id?.type === "required" && <small className="text-danger">Customer PMS Company ID is required</small>}
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <label>
                                            RCM API Key
                                        </label>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            {...register('storageapi_key', { required: true })}
                                            onKeyDown={(e) => { restrictIntialSpace(e) }}
                                            defaultValue={storageapi_key}
                                            className="form-control" readOnly={(LoginUserDetails.role == "super_admin" ? false : true)}
                                        />
                                        {errors.storageapi_key?.type === "required" && <small className="text-danger">RCM API Key is required</small>}
                                    </div>
                                </div>
                            </>
                        )

                }
            </fieldset>
        </>
    )
}

export default MultiPMSconnection;